import { Box, Typography, Chip, Paper, Select, MenuItem } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Droppable } from "@hello-pangea/dnd";
import type { GrowerInfo, GrowerSortOption } from "./types";
import { GrowerCard } from "./GrowerCard";

export function UnassignedGrowersPanel({
	unassignedGrowers,
	growerSortOption,
	setGrowerSortOption,
	selectedGrowers,
	clearSelection,
	handleGrowerClick,
	draggingSelectedCount,
	UNASSIGNED_DROPPABLE_ID,
}: {
	unassignedGrowers: GrowerInfo[];
	growerSortOption: GrowerSortOption;
	setGrowerSortOption: (option: GrowerSortOption) => void;
	selectedGrowers: Set<string>;
	clearSelection: () => void;
	handleGrowerClick: (name: string, shiftKey: boolean) => void;
	draggingSelectedCount: number;
	UNASSIGNED_DROPPABLE_ID: string;
}) {
	return (
		<Box sx={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
				<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
					Unassigned Growers ({unassignedGrowers.length})
				</Typography>
				<Select
					size="small"
					value={growerSortOption}
					onChange={(e: SelectChangeEvent) => setGrowerSortOption(e.target.value as GrowerSortOption)}
					sx={{
						fontSize: "0.75rem",
						height: 28,
						minWidth: 0,
						ml: "auto",
						"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
					}}
				>
					<MenuItem value="name-asc" sx={{ fontSize: "0.8rem" }}>
						Name A-Z
					</MenuItem>
					<MenuItem value="name-desc" sx={{ fontSize: "0.8rem" }}>
						Name Z-A
					</MenuItem>
					<MenuItem value="count-desc" sx={{ fontSize: "0.8rem" }}>
						Jobs High→Low
					</MenuItem>
					<MenuItem value="count-asc" sx={{ fontSize: "0.8rem" }}>
						Jobs Low→High
					</MenuItem>
				</Select>
				{selectedGrowers.size > 0 && (
					<Chip
						label={`${selectedGrowers.size} selected`}
						size="small"
						color="primary"
						onDelete={clearSelection}
						deleteIcon={<Close fontSize="small" />}
						sx={{ flexShrink: 0 }}
					/>
				)}
			</Box>
			<Droppable droppableId={UNASSIGNED_DROPPABLE_ID}>
				{(provided, snapshot) => (
					<Paper
						ref={provided.innerRef}
						{...provided.droppableProps}
						variant="outlined"
						sx={{
							flex: 1,
							p: 1,
							overflowY: "auto",
							bgcolor: snapshot.isDraggingOver ? "action.hover" : "grey.50",
							minHeight: 100,
						}}
					>
						{unassignedGrowers.length === 0 && (
							<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
								All growers assigned
							</Typography>
						)}
						{unassignedGrowers.map((grower, index) => (
							<GrowerCard
								key={grower.name}
								grower={grower}
								index={index}
								isSelected={selectedGrowers.has(grower.name)}
								onToggleSelect={handleGrowerClick}
								multiDragCount={selectedGrowers.has(grower.name) ? draggingSelectedCount : 0}
							/>
						))}
						{provided.placeholder}
					</Paper>
				)}
			</Droppable>
		</Box>
	);
}
